import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * File → downscaled data URL.
 *
 * For the mock backend we persist photos as data URLs in localStorage,
 * which has ~5 MB of headroom across the whole origin. Untouched modern
 * phone photos are 3–8 MB each — well past the budget after 2–3 uploads.
 *
 * We downscale to a max long-side of `MAX_DIMENSION` and re-encode as
 * JPEG at `QUALITY`. Typical output: 80–250 KB per photo. Four photos
 * fit comfortably alongside the rest of the auth/draft state.
 *
 * Supabase swap path:
 *   - Replace this with `supabase.storage.from('profile-photos').upload(...)`
 *   - Return the public URL instead of a data URL
 *   - Components consuming `draft.photos` keep working without changes
 *     because both shapes are `string` URLs
 */

const MAX_DIMENSION = 1024;
const QUALITY = 0.85;

export interface UploadResult {
  ok: boolean;
  /** The downscaled data URL on success. */
  dataUrl?: string;
  /** Renderable error message on failure. */
  error?: string;
}

/**
 * Read a single image file and return a downscaled JPEG data URL.
 *
 * Rejects non-image files and anything that throws while decoding. Never
 * throws — always resolves to `{ ok, error? }` so the caller renders an
 * inline error instead of a crash.
 */
export const downscaleImage = (file: File): Promise<UploadResult> =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve({ ok: false, error: 'That doesn’t look like an image.' });
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => {
      resolve({ ok: false, error: 'Could not read that file.' });
    };

    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        resolve({ ok: false, error: 'Could not read that file.' });
        return;
      }

      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const longest = Math.max(width, height);
        const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
        const w = Math.round(width * scale);
        const h = Math.round(height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve({ ok: false, error: 'Canvas not available.' });
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);

        try {
          const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
          resolve({ ok: true, dataUrl });
        } catch {
          resolve({ ok: false, error: 'Could not encode that image.' });
        }
      };
      img.onerror = () => {
        resolve({ ok: false, error: 'Could not decode that image.' });
      };
      img.src = result;
    };

    reader.readAsDataURL(file);
  });

/** Convenience wrapper for multi-file inputs. Preserves order. */
export const downscaleMany = async (files: FileList | File[]): Promise<UploadResult[]> => {
  const arr = Array.from(files);
  const out: UploadResult[] = [];
  for (const f of arr) {
    out.push(await downscaleImage(f));
  }
  return out;
};

export interface StorageUploadResult {
  ok: boolean;
  /** Storage object path (not a URL) — feed into signPhotoUrl() to render. */
  path?: string;
  error?: string;
}

/**
 * Upload a base64 data URL to the private `profile-photos` bucket and
 * return the storage object path.
 *
 * The bucket is private (see migration `profile_photos_private`), so the
 * caller must mint a signed URL via `signPhotoUrl()` / `signPhotoUrls()`
 * before rendering. We persist the path (not the URL) so signed URLs can
 * be re-minted on each read with a short TTL.
 *
 * Strings that already look like a stored path or absolute https URL are
 * passed through unchanged (idempotent for resumable saves).
 */
export const uploadToStorage = async (
  supabase: SupabaseClient,
  userId: string,
  dataUrl: string,
  index: number,
): Promise<StorageUploadResult> => {
  if (!dataUrl.startsWith('data:')) {
    // Already a stored reference (path or legacy URL). Hand it back as-is.
    return { ok: true, path: dataUrl };
  }

  let blob: Blob;
  try {
    const res = await fetch(dataUrl);
    blob = await res.blob();
  } catch {
    return { ok: false, error: 'Could not read photo data.' };
  }

  const path = `${userId}/${Date.now()}-${index}.jpg`;

  const { error } = await supabase.storage
    .from('profile-photos')
    .upload(path, blob, { contentType: 'image/jpeg', upsert: true });

  if (error) return { ok: false, error: error.message };

  // B1 · UGC media-safety: track every upload as quarantined media and queue
  // moderation. The path is NOT publicly signable until `media-moderate`
  // promotes it to `approved` (see useProfiles gating). Fail soft — a
  // tracking/queue hiccup must never lose the user's uploaded photo.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabase as any;
    const { data: media } = await db
      .from('hottr_media')
      .insert({ owner_id: userId, path, kind: 'profile', status: 'quarantined' })
      .select('id')
      .single();
    if (media?.id) {
      // Fire-and-forget scan. Owner sees their own pending media; others don't.
      void db.functions.invoke('media-moderate', { body: { mediaId: media.id } });
    }
  } catch {
    // hottr_media may not exist pre-migration — storage upload still succeeded.
  }

  return { ok: true, path };
};

/** Default signed-URL TTL — 1 hour. Tunable per call site. */
const DEFAULT_SIGNED_TTL_SECONDS = 60 * 60;

/**
 * Mint a signed URL for a single stored path. Pass-through for absolute
 * URLs (legacy rows from when the bucket was public).
 */
export const signPhotoUrl = async (
  supabase: SupabaseClient,
  path: string,
  ttlSeconds: number = DEFAULT_SIGNED_TTL_SECONDS,
): Promise<string | null> => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const { data, error } = await supabase.storage
    .from('profile-photos')
    .createSignedUrl(path, ttlSeconds);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
};

/**
 * Bulk-sign a list of paths. Order is preserved. Paths that fail to sign
 * resolve to empty strings so callers can `.filter(Boolean)` to drop them.
 */
export const signPhotoUrls = async (
  supabase: SupabaseClient,
  paths: string[],
  ttlSeconds: number = DEFAULT_SIGNED_TTL_SECONDS,
): Promise<string[]> => {
  if (paths.length === 0) return [];

  const passthrough: Record<number, string> = {};
  const toSign: { idx: number; path: string }[] = [];
  paths.forEach((p, idx) => {
    if (!p) return;
    if (p.startsWith('http://') || p.startsWith('https://')) {
      passthrough[idx] = p;
    } else {
      toSign.push({ idx, path: p });
    }
  });

  const signed: Record<number, string> = {};
  if (toSign.length > 0) {
    const { data, error } = await supabase.storage
      .from('profile-photos')
      .createSignedUrls(toSign.map((x) => x.path), ttlSeconds);
    if (!error && data) {
      data.forEach((entry, i) => {
        const target = toSign[i];
        if (entry.signedUrl && target) signed[target.idx] = entry.signedUrl;
      });
    }
  }

  return paths.map((_, idx) => signed[idx] ?? passthrough[idx] ?? '');
};
